import { useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Loader2, ExternalLink } from 'lucide-react';
import api from '../../lib/api';
import { inr, nc } from './IsaKpis';

const crmLeadUrl = id => `https://crm.zoho.com/crm/org690252559/tab/Leads/${id}`;
const when = v => (v ? new Date(v).toLocaleString('en-IN', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }) : '');

export const IsaDayDialog = ({ day, onClose }) => {
  const [d, setD] = useState(null);
  useEffect(() => {
    setD(null);
    if (day) api.get(`/inside-sales-audit/day/${day}`).then(r => setD(r.data)).catch(() => setD({ error: true }));
  }, [day]);
  return (
    <Dialog open={!!day} onOpenChange={o => !o && onClose()}>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="isa-day-dialog">
        <DialogHeader><DialogTitle>Snapshot · {day}</DialogTitle></DialogHeader>
        {!d && <div className="py-10 text-center text-slate-400"><Loader2 className="w-5 h-5 animate-spin inline" /></div>}
        {d?.error && <p className="text-sm text-rose-600">Could not load this snapshot.</p>}
        {d && !d.error && (
          <div className="space-y-4 text-xs">
            <p className="text-slate-500">Saved {when(d.captured_at)} by {d.captured_by} ({d.kind}). Fingerprint <span className="font-mono">{d.hash.slice(0, 12)}</span> — CRM counts cannot be edited; only a backdated rule change can recalculate the money.</p>
            <div className="bg-slate-50 rounded-md p-3 space-y-1" data-testid="isa-day-calc">
              {d.calc.lines.map(l => <div key={l} className="text-slate-700">{l}</div>)}
            </div>
            {Object.entries(d.people).map(([k, p]) => (
              <div key={k} data-testid={`isa-day-person-${k}`}>
                <div className="font-bold text-slate-900 mb-1">{p.name} — {p.hot} Hot · Unable to Reach {nc(p.utr)} · Call Back {nc(p.callback)}</div>
                {p.hot_leads.length === 0 ? <p className="text-slate-400">No Hot conversions this day.</p> : (
                  <table className="w-full table-fixed">
                    <colgroup><col className="w-[34%]" /><col className="w-[20%]" /><col className="w-[36%]" /><col className="w-[10%]" /></colgroup>
                    <tbody>
                      {p.hot_leads.map(x => (
                        <tr key={x.id} className="border-b border-slate-50">
                          <td className="py-1 pr-2 text-slate-800 truncate">{x.name || x.id}</td>
                          <td className="py-1 pr-2 text-slate-500">{when(x.hot_at)}</td>
                          <td className="py-1 pr-2 text-slate-500 truncate">now: {x.status} · {x.owner}</td>
                          <td className="py-1 text-right"><a href={crmLeadUrl(x.id)} target="_blank" rel="noreferrer" className="text-blue-600 inline-flex items-center gap-1">CRM <ExternalLink className="w-3 h-3" /></a></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            ))}
            {(d.reconciliations || []).length > 0 && (
              <div data-testid="isa-day-recon">
                <div className="font-bold text-slate-900 mb-1">Reconciliation checks</div>
                {d.reconciliations.map(r => (
                  <div key={r.id} className={r.matches ? 'text-emerald-700' : 'text-amber-700'}>
                    {when(r.at)} by {r.by}: {r.matches ? 'CRM matches the snapshot' : Object.entries(r.diff).map(([k, v]) => `${k}: snapshot ${v.snapshot}, CRM now ${v.crm}`).join('; ')}
                  </div>
                ))}
              </div>
            )}
            {(d.recalculations || []).length > 0 && (
              <div data-testid="isa-day-recalcs">
                <div className="font-bold text-slate-900 mb-1">Recalculation history</div>
                {d.recalculations.map(r => (
                  <div key={r.at} className="text-slate-600">{when(r.at)} by {r.by}: rules v{r.from_version || 1} → v{r.to_version}, net {inr(r.old_net)} → {inr(r.new_net)}{r.note ? ` — ${r.note}` : ''}</div>
                ))}
              </div>
            )}
            <p className="text-[11px] text-slate-400">Net {inr(d.calc.net)} · rules version {d.config_version || 1}</p>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
